import * as React from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from 'src/components/Breadcrumb';
import BreadcrumbItem from 'src/components/Breadcrumb/BreadcrumbItem';
import Title from 'src/components/Title';
import OrganizerSelect from './components/OrganizerSelect';
import Radio from 'src/components/FormElements/Radio';
import DatePicker from 'src/components/DatePicker';
import Input from 'src/components/FormElements/Input';
import report1preview from 'src/images/report1.jpg';
import report2preview from 'src/images/report2.jpg';
import classNames from 'classnames';
import { GetReportParams, ReportResult, getFiuNumbersAndParticipants3FCoursePortalReport, getParticipantsCompleted3FCoursePortalReport } from 'src/helpers/api';
import { DotLoader } from 'src/components/Loaders';

type PeriodType = 'custom' | 'year_to_date';

const ReportDistribution = () => {
  const [organizers, setOrganizers] = React.useState<number[]>([]);
  const [periodType, setPeriodType] = React.useState<PeriodType>('year_to_date');
  const [dateFrom, setDateFrom] = React.useState<Date | null>(null);
  const [dateTo, setDateTo] = React.useState<Date | null>(null);
  const [isGeneratingReport1, setIsGeneratingReport1] = React.useState<boolean>(false);
  const [isGeneratingReport2, setIsGeneratingReport2] = React.useState<boolean>(false);

  const isValid = React.useMemo(() => {
    if (organizers.length === 0) {
      return false;
    }

    if (periodType === 'custom' && (!dateFrom || !dateTo)) {
      return false;
    }

    return true;
  }, [organizers, periodType, dateFrom, dateTo]);

  const handleGenerateReport = (reportType: string) => {
    if (!isValid) {
      return;
    }

    let dateFromParam: Date = periodType === 'custom' && dateFrom ? dateFrom : new Date();
    let dateToParam: Date = periodType === 'custom' && dateTo ? dateTo : new Date();

    if (periodType === 'year_to_date') {
      dateFromParam = new Date(dateFromParam.getFullYear(), 0, 1);
      dateToParam = new Date();
    }

    const params: GetReportParams = {
      organizers,
      fromDate: dateFromParam.toISOString().split('T')[0],
      toDate: dateToParam.toISOString().split('T')[0],
    };
    let promise: null | Promise<ReportResult> = null;

    if (reportType === 'report1') {
      promise = getFiuNumbersAndParticipants3FCoursePortalReport(params);
      setIsGeneratingReport1(true);
    } else {
      promise = getParticipantsCompleted3FCoursePortalReport(params);
      setIsGeneratingReport2(true);
    }

    if (promise !== null)  {
      promise
        .then((result) => {
          window.location.href = result.downloadUrl;
        })
        .finally(() => {
          if (reportType === 'report1') {
            setIsGeneratingReport1(false);
          } else {
            setIsGeneratingReport2(false);
          }
        });
    }
  };

  return (
    <div className="has-background-gray-body pb-14">
      <div className="wrapper">
        <Breadcrumb className="py3">
          <BreadcrumbItem>
            <Link to="/">Forside</Link>
          </BreadcrumbItem>
          <BreadcrumbItem active={true}>
            <Link to="">Fordeling</Link>
          </BreadcrumbItem>
        </Breadcrumb>
        <Title className="mb3">Statistik & fordeling</Title>
        <div className="has-background-white lg:flex p-8">
          {/* Sidebar */}
          <div className="w-full lg:w-64 xl:w-80 flex-none mr-8">
            <label className="block f4 font-semibold uppercase mb1" htmlFor="period">Periode</label>
            <div className="flex flex-column">
              <Radio
                name="periodType"
                checked={periodType === 'year_to_date'}
                onChange={() => setPeriodType('year_to_date')}
                className="mt1 cursor-pointer"
              >
                År til dato
              </Radio>
              <Radio
                name="periodType"
                checked={periodType === 'custom'}
                onChange={() => setPeriodType('custom')}
                className="mt1 cursor-pointer"
              >
                Vælg periode
              </Radio>
              {periodType === 'custom' && (
                <>
                  <label className="mt-4 f6 mb-1 ttu">Startdato</label>
                  <DatePicker
                    placeholderText="Tryk for at vælge data"
                    wrapperClassName="block w-full mb-2"
                    name="fromDate"
                    id="fromDate"
                    selected={dateFrom}
                    startDate={dateFrom}
                    endDate={dateTo}
                    selectsStart={true}
                    customInput={<Input />}
                    onChange={(date) => {
                      if (date instanceof Date) {
                        setDateFrom(date);
                      }
                    }}
                  />
                  <label className="f6 mb-1 ttu">Slutdato</label>
                  <DatePicker
                    placeholderText="Tryk for at vælge data"
                    wrapperClassName="block w-full"
                    name="dateTo"
                    id="dateTo"
                    selected={dateTo}
                    startDate={dateFrom}
                    endDate={dateTo}
                    selectsEnd={true}
                    customInput={<Input />}
                    onChange={(date) => {
                      if (date instanceof Date) {
                        setDateTo(date);
                      }
                    }}
                  />
                </>
              )}
            </div>

            <label
              className="block f4 font-semibold uppercase mt3 mb1"
              htmlFor="organizer"
            >
              Arrangør
            </label>
            <OrganizerSelect
              value={organizers}
              onChange={setOrganizers}
              initiallySelectAll={true}
            />
          </div>
          <div>
            <h3 className="block f4 font-semibold uppercase mb2 mt0">Vælg rapportvisning</h3>
            <div className="columns has-large-gutter">
              <button
                type="button"
                className={classNames(
                  'column is-lg-6 relative',
                  isValid ? 'cursor-pointer' : 'cursor-default opacity-75',
                  isGeneratingReport1 && 'cursor-default',
                )}
                onClick={() => handleGenerateReport('report1')}
                disabled={isValid === false || isGeneratingReport1}
              >
                {isGeneratingReport1 && (
                  <div className="absolute inset-0 bg-white-opaque-75 flex items-center justify-center">
                    <DotLoader />
                  </div>
                )}
                <div className="p-4 shadow-sm mb-8">
                  <img className="w-full" src={report1preview} />
                </div>
                <div
                  className={classNames(
                    'button is-primary',
                    isValid === false && 'disabled',
                    isGeneratingReport1 && 'is-loading',
                  )}
                >
                  Generer rapportvisning 1
                </div>
              </button>
              <button
                type="button"
                className={classNames(
                  'column is-lg-6',
                  isValid ? 'cursor-pointer' : 'cursor-default opacity-75',
                  isGeneratingReport2 && 'cursor-default',
                )}
                onClick={() => handleGenerateReport('report2')}
                disabled={isValid === false || isGeneratingReport2}
              >
                {isGeneratingReport2 && (
                  <div className="absolute inset-0 bg-white-opaque-75 flex items-center justify-center">
                    <DotLoader />
                  </div>
                )}
                <div className="p-4 shadow-sm mb-8">
                  <img className="w-full" src={report2preview} />
                </div>
                <div
                  className={classNames(
                    'button is-primary',
                    isValid === false && 'disabled',
                    isGeneratingReport2 && 'is-loading',
                  )}
                >
                  Generer rapportvisning 2
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportDistribution;



// WEBPACK FOOTER //
// ./src/Pages/CoursePortal/Reports/Distribution.tsx